import React, { useMemo, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import ArticleIcon from "@mui/icons-material/Article";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import garahexLogo from "../assets/garahex-logo-white.png";
import { ActionButton, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const PUBLIC_BASE_URL = "https://garahex.com";

const previewModes = [
  { key: "desktop", label: "Desktop", icon: <DesktopWindowsOutlinedIcon fontSize="small" />, width: "100%" },
  { key: "tablet", label: "Tablet", icon: <TabletMacOutlinedIcon fontSize="small" />, width: { xs: "100%", md: 560 } },
  { key: "mobile", label: "Mobile", icon: <PhoneIphoneOutlinedIcon fontSize="small" />, width: { xs: "100%", md: 340 } }
];

const pageCards = [
  {
    title: "Landing Page",
    route: "/",
    managePath: "/website-management/landing",
    icon: <ArticleIcon />,
    tone: "info",
    description: "Primary public homepage for Garahex logistics services, booking entry, services, how-it-works, CTA, and footer.",
    sections: ["Hero", "Quick Quote", "Services", "How It Works", "CTA", "Footer"],
    type: "Marketing page"
  },
  {
    title: "About Us",
    route: "/about-us",
    managePath: "/website-management/about",
    icon: <InfoOutlinedIcon />,
    tone: "success",
    description: "Public company story page using the existing static page structure and shared public header/footer.",
    sections: ["Hero", "Company Story", "Mission", "Vision", "Values", "CTA"],
    type: "Brand page"
  },
  {
    title: "Contact Us",
    route: "/contact-us",
    managePath: "/website-management/contact",
    icon: <ContactSupportOutlinedIcon />,
    tone: "purple",
    description: "Public contact surface backed by existing company settings and social/contact links. No office map section is introduced.",
    sections: ["Hero", "Contact Details", "Phone", "Email", "Social Links"],
    type: "Support page"
  },
  {
    title: "Terms & Conditions",
    route: "/term-condition",
    managePath: "/website-management/terms",
    icon: <GavelOutlinedIcon />,
    tone: "warning",
    description: "Continuous legal document page for the existing public terms route.",
    sections: ["Document Layout", "Legal Sections", "Footer"],
    type: "Document page"
  },
  {
    title: "Privacy Policy",
    route: "/privacy-policy",
    managePath: "/website-management/privacy",
    icon: <PolicyOutlinedIcon />,
    tone: "info",
    description: "Continuous privacy document page for the existing public privacy-policy route.",
    sections: ["Document Layout", "Information Use", "Cookie Policy", "Footer"],
    type: "Document page"
  }
];

const settingsCards = [
  { title: "General", description: "Website identity and public page routing summary.", tone: "info" },
  { title: "Navigation", description: "Public page links and admin Website group destinations.", tone: "success" },
  { title: "Branding", description: "Logo, brand name, tagline, and color system summary.", tone: "warning" },
  { title: "Contact Information", description: "Company contact fields are managed by existing app settings where available.", tone: "purple" },
  { title: "Social Links", description: "Existing public contact page can render configured social links.", tone: "info" },
  { title: "Page Management", description: "Detailed page editor surfaces are reserved for UI-9.", tone: "success" }
];

const liveUrlFor = (route) => `${PUBLIC_BASE_URL}${route === "/" ? "/" : route}`;

const PreviewShell = ({ mode, selectedPage }) => (
  <Box
    sx={{
      width: mode.width,
      maxWidth: "100%",
      mx: "auto",
      borderRadius: 5,
      overflow: "hidden",
      border: `1px solid ${adminTokens.colors.border}`,
      background: "#FFFFFF",
      boxShadow: "0 24px 56px rgba(3, 29, 54, 0.12)"
    }}
  >
    <Box
      sx={{
        height: 38,
        px: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 0.8,
        background: "linear-gradient(135deg, #061A33, #0B4FA3)",
        color: "#FFFFFF"
      }}
    >
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: adminTokens.colors.garahexOrange }} />
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: adminTokens.colors.warning }} />
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: adminTokens.colors.success }} />
      <Typography sx={{ ml: 1, fontSize: 12, fontWeight: 800, opacity: 0.86 }}>{liveUrlFor(selectedPage.route)}</Typography>
    </Box>
    <Box sx={{ p: { xs: 2, md: 2.5 }, minHeight: 260, background: "radial-gradient(circle at top right, rgba(255,107,0,.14), transparent 32%), linear-gradient(180deg, #FFFFFF, #F7FBFF)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box sx={{ width: 130 }}>
          <img src={garahexLogo} alt="Garahex logo" style={{ display: "block", width: "100%", height: "auto" }} />
        </Box>
        <StatusBadge tone={selectedPage.tone}>Live Page</StatusBadge>
      </Box>
      <Typography component="h2" sx={{ color: adminTokens.colors.deepNavy, fontWeight: 950, fontSize: { xs: "1.45rem", md: "1.9rem" }, lineHeight: 1.05 }}>
        {selectedPage.title}
      </Typography>
      <Typography sx={{ mt: 1, color: adminTokens.colors.muted, maxWidth: 620, fontSize: "0.95rem" }}>
        {selectedPage.description}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2.5 }}>
        {selectedPage.sections.slice(0, 5).map((section) => (
          <StatusBadge key={section} tone="info">{section}</StatusBadge>
        ))}
      </Box>
    </Box>
  </Box>
);

export default function WebsiteManagement() {
  const [selectedRoute, setSelectedRoute] = useState("/");
  const [previewMode, setPreviewMode] = useState("desktop");

  const selectedPage = useMemo(() => pageCards.find((page) => page.route === selectedRoute) || pageCards[0], [selectedRoute]);
  const selectedMode = previewModes.find((mode) => mode.key === previewMode) || previewModes[0];

  return (
    <Box className="garahex-website-management-page">
      <PageHeader
        eyebrow="Website"
        title="Website Management"
        description="Manage, preview, and maintain the Garahex public website from one admin workspace."
        actions={
          <>
            <Button component="a" href={PUBLIC_BASE_URL} target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<OpenInNewIcon />} sx={{ borderRadius: 999, fontWeight: 850, textTransform: "none" }}>
              View Website
            </Button>
            <ActionButton component={Link} to="/settings">Website Settings</ActionButton>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <SurfaceCard sx={{ minHeight: 174, background: `linear-gradient(135deg, ${adminTokens.colors.deepNavy}, #0B4FA3)`, color: "#FFFFFF" }}>
            <Box sx={{ width: 154, mb: 2 }}>
              <img src={garahexLogo} alt="Garahex logo" style={{ display: "block", width: "100%", height: "auto" }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 850, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(255,255,255,.72)" }}>GARAHEX Website</Typography>
            <Typography sx={{ mt: 0.8, fontSize: "1.4rem", fontWeight: 950 }}>garahex.com</Typography>
            <Typography sx={{ mt: 0.5, color: "rgba(255,255,255,.78)" }}>Brand: GARAHEX · Tagline: GARAHE EXPRESS</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SurfaceCard sx={{ minHeight: 174 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Public Pages</Typography>
            <Typography sx={{ color: adminTokens.colors.text, fontSize: "2rem", fontWeight: 950, mt: 0.8 }}>{pageCards.length}</Typography>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13 }}>Actual routed pages</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SurfaceCard sx={{ minHeight: 174 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Persistence</Typography>
            <Typography sx={{ color: adminTokens.colors.warning, fontSize: "1.1rem", fontWeight: 950, mt: 1.2 }}>Not Available</Typography>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13 }}>No new CMS backend created</Typography>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} md={3}>
          <SurfaceCard sx={{ minHeight: 174 }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>Quick Actions</Typography>
            <Box sx={{ mt: 1.5, display: "grid", gap: 1 }}>
              <Button component={Link} to="/website-management/landing" variant="outlined" sx={{ justifyContent: "flex-start", borderRadius: 3, textTransform: "none" }}>Manage Landing Page</Button>
              <Button component={Link} to="/website-management/contact" variant="outlined" sx={{ justifyContent: "flex-start", borderRadius: 3, textTransform: "none" }}>Manage Contact Us</Button>
              <Button component={Link} to="/website-management/branding" variant="outlined" sx={{ justifyContent: "flex-start", borderRadius: 3, textTransform: "none" }}>Branding / Settings</Button>
            </Box>
          </SurfaceCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <SurfaceCard>
            <SectionHeader
              title="Public Page Management"
              description="Open the live public routes and review the page-management surfaces approved for this UI stage."
            />
            <Grid container spacing={2}>
              {pageCards.map((page) => (
                <Grid item xs={12} md={6} key={page.route}>
                  <SurfaceCard
                    component="article"
                    tabIndex={0}
                    sx={{
                      height: "100%",
                      border: selectedRoute === page.route ? `1px solid rgba(255, 107, 0, 0.45)` : undefined,
                      boxShadow: selectedRoute === page.route ? "0 18px 40px rgba(255, 107, 0, 0.14)" : undefined
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
                      <Box sx={{ width: 48, height: 48, display: "grid", placeItems: "center", borderRadius: 3.2, color: adminTokens.colors.garahexOrange, background: "rgba(255,107,0,.12)" }}>
                        {page.icon}
                      </Box>
                      <StatusBadge tone={page.tone}>Live Page</StatusBadge>
                    </Box>
                    <Typography component="h2" sx={{ color: adminTokens.colors.deepNavy, fontWeight: 950, fontSize: "1.1rem" }}>{page.title}</Typography>
                    <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850, mt: 0.3 }}>{page.route}</Typography>
                    <Typography sx={{ color: adminTokens.colors.text, mt: 1, minHeight: 58 }}>{page.description}</Typography>
                    <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", mt: 1.6 }}>
                      <StatusBadge tone="info">{page.type}</StatusBadge>
                      {page.sections.slice(0, 2).map((section) => <StatusBadge key={section} tone="success">{section}</StatusBadge>)}
                    </Box>
                    <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button onClick={() => setSelectedRoute(page.route)} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>Preview</Button>
                      <Button component="a" href={liveUrlFor(page.route)} target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<OpenInNewIcon />} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>
                        Open Live Page
                      </Button>
                      <Button component={Link} to={page.managePath} variant="text" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>Manage Page</Button>
                    </Box>
                  </SurfaceCard>
                </Grid>
              ))}
            </Grid>
          </SurfaceCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <SurfaceCard sx={{ mb: 2 }}>
            <SectionHeader
              title="Responsive Preview"
              description="Frontend-only preview surface for the selected public page. It does not embed or modify the live site."
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {previewModes.map((mode) => (
                <Button
                  key={mode.key}
                  onClick={() => setPreviewMode(mode.key)}
                  startIcon={mode.icon}
                  variant={previewMode === mode.key ? "contained" : "outlined"}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                >
                  {mode.label}
                </Button>
              ))}
            </Box>
            <PreviewShell mode={selectedMode} selectedPage={selectedPage} />
          </SurfaceCard>

          <SurfaceCard sx={{ mb: 2 }}>
            <SectionHeader title="Branding Summary" description="Correct existing Garahex logo and established admin design tokens." />
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ width: 156 }}>
                <img src={garahexLogo} alt="Garahex logo" style={{ display: "block", width: "100%", height: "auto" }} />
              </Box>
              <Box>
                <Typography sx={{ color: adminTokens.colors.deepNavy, fontSize: "1.2rem", fontWeight: 950 }}>GARAHEX</Typography>
                <Typography sx={{ color: adminTokens.colors.muted, fontWeight: 850 }}>GARAHE EXPRESS</Typography>
                <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap", mt: 1 }}>
                  <StatusBadge tone="info">Deep Navy</StatusBadge>
                  <StatusBadge tone="warning">Garahex Orange</StatusBadge>
                  <StatusBadge tone="success">Success Green</StatusBadge>
                </Box>
              </Box>
            </Box>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader title="Website Settings Summary" description="Current UI categories only; no new persistence or backend writes are introduced." />
            <Grid container spacing={1.2}>
              {settingsCards.map((card) => (
                <Grid item xs={12} sm={6} key={card.title}>
                  <Box sx={{ border: `1px solid ${adminTokens.colors.border}`, borderRadius: 3, p: 1.4, minHeight: 110, background: "#FFFFFF" }}>
                    <StatusBadge tone={card.tone}>{card.title}</StatusBadge>
                    <Typography sx={{ color: adminTokens.colors.muted, mt: 1, fontSize: 13 }}>{card.description}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SurfaceCard>
        </Grid>
      </Grid>
    </Box>
  );
}
